import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Users, Store, UserPlus, Link as LinkIcon, Copy, UserCog, Crown, Shield } from 'lucide-react';

export default function Teams({ user, selectedTeam, onTeamChange }) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    bot_token: '',
    channel_id: '',
    info_channel_id: '',
    main_channel_url: ''
  });
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteTeamId, setInviteTeamId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [currentTeamMembers, setCurrentTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    loadTeams();
  }, []);

  const getUserRoleInTeam = (team) => {
    if (team.owner_id === user.id) return 'owner';
    // Role will be loaded with teams data later
    return team.user_role || 'member';
  };

  const canManageTeam = (team) => {
    const role = getUserRoleInTeam(team);
    return role === 'owner' || role === 'admin';
  };

  const loadTeams = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/teams');
      const teamsData = res.data;
      
      // Load user's role in each team
      const teamsWithRoles = await Promise.all(
        teamsData.map(async (team) => {
          if (team.owner_id === user.id) {
            return { ...team, user_role: 'owner' };
          }
          try {
            const membersRes = await axiosInstance.get(`/teams/${team.id}/members`);
            const member = membersRes.data.find(m => m.user_id === user.id);
            return { ...team, user_role: member ? member.role : 'member' };
          } catch {
            return { ...team, user_role: 'member' };
          }
        })
      );
      
      setTeams(teamsWithRoles);
    } catch (err) {
      toast.error('Mağazalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeam) {
        await axiosInstance.put(`/teams/${editingTeam.id}`, formData);
        toast.success('Mağaza güncellendi');
      } else {
        const res = await axiosInstance.post('/teams', formData);
        toast.success('Mağaza oluşturuldu');
        onTeamChange(res.data.id);
      }
      setDialogOpen(false);
      setFormData({ name: '', bot_token: '', channel_id: '', info_channel_id: '', main_channel_url: '' });
      setEditingTeam(null);
      loadTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      bot_token: team.bot_token || '',
      channel_id: team.channel_id || '',
      info_channel_id: team.info_channel_id || '',
      main_channel_url: team.main_channel_url || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Bu mağazayı silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axiosInstance.delete(`/teams/${teamId}`);
      toast.success('Mağaza silindi');
      if (selectedTeam === teamId) {
        onTeamChange(null);
      }
      loadTeams();
    } catch (err) {
      toast.error('Mağaza silinemedi');
    }
  };

  const openNewDialog = () => {
    setEditingTeam(null);
    setFormData({ name: '', bot_token: '', channel_id: '', info_channel_id: '' });
    setDialogOpen(true);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await axiosInstance.post(`/teams/${inviteTeamId}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      
      // Get invitation link
      const invitationLink = `${window.location.origin}/accept-invitation?token=${res.data.token}`;
      
      // Show success with link
      toast.success('Davet gönderildi ve link oluşturuldu!');
      
      // Show link in a copyable format
      navigator.clipboard.writeText(invitationLink);
      toast.info('Davet linki kopyalandı!');
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setGeneratedLink('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Davet gönderilemedi');
    } finally {
      setInviting(false);
    }
  };

  const handleGenerateLink = async () => {
    setInviting(true);
    try {
      const payload = inviteEmail ? { email: inviteEmail, role: inviteRole } : { role: inviteRole };
      const res = await axiosInstance.post(`/teams/${inviteTeamId}/invite`, payload);
      
      const invitationLink = `${window.location.origin}/accept-invitation?token=${res.data.token}`;
      setGeneratedLink(invitationLink);
      toast.success(inviteEmail ? 'Davet linki oluşturuldu!' : 'Genel davet linki oluşturuldu!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link oluşturulamadı');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toast.success('Link kopyalandı!');
    }
  };

  const loadTeamMembers = async (teamId) => {
    setLoadingMembers(true);
    try {
      const res = await axiosInstance.get(`/teams/${teamId}/members`);
      setCurrentTeamMembers(res.data);
    } catch (err) {
      toast.error('Üyeler yüklenemedi');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleViewMembers = (teamId) => {
    setInviteTeamId(teamId);
    loadTeamMembers(teamId);
    setMembersDialogOpen(true);
  };

  const isTeamOwner = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team && team.owner_id === user.id;
  };

  const isTeamOwnerOrAdmin = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return false;
    
    // Owner always has access
    if (team.owner_id === user.id) return true;
    
    // Check if user is admin member
    const member = currentTeamMembers.find(m => m.user_id === user.id);
    return member && member.role === 'admin';
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Bu üyeyi mağazadan çıkarmak istediğinizden emin misiniz?')) return;
    
    try {
      await axiosInstance.delete(`/teams/${inviteTeamId}/members/${memberId}`);
      toast.success('Üye çıkarıldı');
      loadTeamMembers(inviteTeamId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Üye çıkarılamadı');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await axiosInstance.put(`/teams/${inviteTeamId}/members/${memberId}/role?role=${newRole}`);
      toast.success('Rol güncellendi');
      loadTeamMembers(inviteTeamId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rol güncellenemedi');
    }
  };

  return (
    <div className="p-8" data-testid="teams-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Mağazalar</h1>
          <p className="text-gray-600">Mağazalarınızı yönetin</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openNewDialog}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
              data-testid="create-team-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Yeni Mağaza
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="team-dialog">
            <DialogHeader>
              <DialogTitle>{editingTeam ? 'Mağazayı Düzenle' : 'Yeni Mağaza Oluştur'}</DialogTitle>
              <DialogDescription>
                Mağaza bilgilerini girin. Bot token ve kanal ID&apos;yi sonra da ekleyebilirsiniz.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Mağaza Adı *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Delfiee Store"
                    required
                    data-testid="team-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bot_token">Telegram Bot Token</Label>
                  <Input
                    id="bot_token"
                    value={formData.bot_token}
                    onChange={(e) => setFormData({ ...formData, bot_token: e.target.value })}
                    placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    data-testid="bot-token-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="channel_id">Telegram Kanal ID</Label>
                  <Input
                    id="channel_id"
                    value={formData.channel_id}
                    onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                    placeholder="-1001234567890"
                    data-testid="channel-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="info_channel_id">Telegram Info Kanal ID (Opsiyonel)</Label>
                  <Input
                    id="info_channel_id"
                    value={formData.info_channel_id}
                    onChange={(e) => setFormData({ ...formData, info_channel_id: e.target.value })}
                    placeholder="-1009876543210"
                    data-testid="info-channel-id-input"
                  />
                  <p className="text-xs text-gray-500">Menü buraya da gönderilir (sabitlenmez, her yeni menüde bir öncekini siler). Duyurularda da hedef olarak seçilebilir.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="main_channel_url">Ana Kanal URL (Opsiyonel)</Label>
                  <Input
                    id="main_channel_url"
                    value={formData.main_channel_url}
                    onChange={(e) => setFormData({ ...formData, main_channel_url: e.target.value })}
                    placeholder="https://t.me/kanal_adi veya davet linki"
                    data-testid="main-channel-url-input"
                  />
                  <p className="text-xs text-gray-500">Info kanala gönderilen menünün başına eklenen "📢 MAIN CHANNEL" butonunun hedefi. Boş bırakılırsa otomatik çözümlenir (bot admin olmalı).</p>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-gradient-to-r from-purple-500 to-pink-500" data-testid="save-team-button">
                  {editingTeam ? 'Güncelle' : 'Oluştur'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : teams.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <Store className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">Hiç mağaza yok</h3>
            <p className="text-gray-600 mb-4">Yeni bir mağaza oluşturarak başlayın</p>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map(team => (
            <Card key={team.id} className="shadow-lg hover:shadow-xl transition-all border-0" data-testid={`team-card-${team.name}`}>
              <CardHeader className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Store className="w-6 h-6" />
                    <CardTitle className="text-xl">{team.name}</CardTitle>
                  </div>
                  {canManageTeam(team) && (
                    <div className="flex space-x-2">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="hover:bg-white/20"
                        onClick={() => handleEdit(team)}
                        data-testid={`edit-team-${team.name}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="hover:bg-red-500/20"
                        onClick={() => handleDelete(team.id)}
                        data-testid={`delete-team-${team.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Bot Token:</span>
                    <span className={`font-medium ${team.bot_token ? 'text-green-600' : 'text-red-600'}`}>
                      {team.bot_token ? '✓ Ayarlandı' : '✗ Yok'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Kanal ID:</span>
                    <span className={`font-medium ${team.channel_id ? 'text-green-600' : 'text-red-600'}`}>
                      {team.channel_id ? '✓ Ayarlandı' : '✗ Yok'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Info Kanal ID:</span>
                    <span className={`font-medium ${team.info_channel_id ? 'text-green-600' : 'text-gray-400'}`}>
                      {team.info_channel_id ? '✓ Ayarlandı' : '— Opsiyonel'}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button 
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        if (team.id !== selectedTeam) {
                          onTeamChange(team.id);
                          toast.success(`${team.name} seçildi`);
                          navigate('/');
                        }
                      }}
                      disabled={team.id === selectedTeam}
                      data-testid={`select-team-${team.name}`}
                    >
                      {team.id === selectedTeam ? 'Seçili' : 'Bu Mağazayı Seç'}
                    </Button>
                    {canManageTeam(team) && (
                      <>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleViewMembers(team.id)}
                          title="Üyeleri Görüntüle"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setInviteTeamId(team.id);
                            setInviteDialogOpen(true);
                          }}
                          data-testid={`invite-to-${team.name}`}
                          title="Üye Davet Et"
                        >
                          <UserPlus className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
        setInviteDialogOpen(open);
        if (!open) {
          setGeneratedLink('');
          setInviteEmail('');
          setInviteRole('member');
        }
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Mağazaya Üye Davet Et</DialogTitle>
            <DialogDescription className="text-sm">
              Davet linkini oluşturun ve istediğiniz şekilde paylaşın
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite_email" className="text-sm sm:text-base">Email (Opsiyonel)</Label>
              <Input
                id="invite_email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Genel link için boş bırakın"
                className="text-sm sm:text-base"
              />
              <p className="text-xs text-gray-500">
                {inviteEmail ? 'Bu e-posta sahibi davet linkini kullanabilir' : 'E-posta belirtmezseniz herkes kullanabilir'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite_role" className="text-sm sm:text-base">Rol *</Label>
              <select
                id="invite_role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full h-10 px-3 rounded-2xl border border-gray-300 text-sm sm:text-base"
              >
                <option value="member">Üye</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {generatedLink && (
              <div className="space-y-2 p-3 bg-green-50 rounded-2xl border border-green-200">
                <Label className="text-sm font-semibold text-green-800">Davet Linki Oluşturuldu!</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={generatedLink}
                    readOnly
                    className="text-xs sm:text-sm bg-white"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={handleCopyLink}
                    title="Kopyala"
                    className="flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-green-700">Bu linki istediğiniz kişiyle paylaşabilirsiniz</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              type="button"
              variant="outline"
              onClick={handleGenerateLink}
              disabled={inviting}
              className="w-full sm:w-auto"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              {inviting ? 'Oluşturuluyor...' : 'Link Oluştur'}
            </Button>
            {inviteEmail && (
              <Button 
                type="button"
                onClick={handleInvite}
                className="bg-gradient-to-r from-purple-500 to-pink-500 w-full sm:w-auto" 
                disabled={inviting}
              >
                {inviting ? 'Gönderiliyor...' : 'E-posta ile Davet Gönder'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Management Dialog */}
      <Dialog open={membersDialogOpen} onOpenChange={setMembersDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Mağaza Üyeleri
            </DialogTitle>
            <DialogDescription className="text-sm">
              Mağazaya erişimi olan kullanıcıları yönetin
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {loadingMembers ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : currentTeamMembers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">Henüz üye yok</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {currentTeamMembers.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        {member.role === 'owner' ? (
                          <Crown className="w-5 h-5 text-white" />
                        ) : member.role === 'admin' ? (
                          <Shield className="w-5 h-5 text-white" />
                        ) : (
                          <Users className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm sm:text-base truncate">{member.username || member.email}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {member.role === 'owner' ? (
                        <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-yellow-100 text-yellow-800 rounded-full font-medium">
                          Sahip
                        </span>
                      ) : isTeamOwnerOrAdmin(inviteTeamId) ? (
                        <>
                          <select
                            value={member.role}
                            onChange={(e) => handleChangeRole(member.id, e.target.value)}
                            className="text-xs sm:text-sm px-2 py-1 border rounded-2xl"
                          >
                            <option value="member">Üye</option>
                            <option value="admin">Admin</option>
                          </select>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 hover:bg-red-100 hover:text-red-600"
                            onClick={() => handleRemoveMember(member.id)}
                            title="Çıkar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-full">
                          {member.role === 'admin' ? 'Admin' : 'Üye'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isTeamOwnerOrAdmin(inviteTeamId) && (
            <DialogFooter>
              <Button 
                variant="outline"
                onClick={() => {
                  setMembersDialogOpen(false);
                  setInviteDialogOpen(true);
                }}
                className="w-full sm:w-auto"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Yeni Üye Davet Et
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
